import template from 'lodash-es/template';
import { T_ } from '@shared/translation';
import { qtsp } from '@shared/tracking-qtsbinds';
import Modal, { SIZE as MODAL_SIZE } from '@shared/ui-modal';
import createGeocoding, {
  AUTOCOMPLETE_PREFERENCE,
} from '@shared/local-jobs-geocoding';
import debounce from 'lodash-es/debounce';
import {
  initialPageNumber,
  showAlertModal,
  pageName,
  gotoUrl,
  exchangerate,
  initialTotalEntries,
  resultSetSize,
  defaultResultSetSize,
  showPostBtn,
  showBidBtn,
} from 'server-data/job-search-new';
import { siteName } from 'server-data/flconfigs';
import getRemoteData from './apiCall';
import renderTable, { bindElement as bindTableCard } from './renderTable';
import JobSearchSelector from './JobSearchSelector';
import Pagination from './pagination';
import SearchHistory from './searchHistory';
import jobAlertModalHeaderTemplate from './templates/jobAlertModalHeaderTemplate.html';
import jobAlertModalTemplate from './templates/jobAlertModalTemplate.html';
import otherRelatedTemplate from './templates/tagsTemplate.html';

const DEFAULT_LOCATION = 'Online Job';
const DEFAULT_HOURLY_DURATION = 'false';
const SCROLL_SPEED = 600;
const JOBS_PAGE = 'JobsPage';
// Page Title
const jobLocationHeroTitleEl = document.getElementById(
  'job-hero-location-title',
);
const breadcrumbSearchPageEl = document.getElementById(
  'breadcrumb-search-page',
);
const breadcrumbSearchTitleEl = document.getElementById(
  'breadcrumb-search-title',
);
const breadcrumbSearchTitleUrlEl = document.getElementById(
  'breadcrumb-search-title-url',
);

// Search and Filter
const searchFormEl = document.getElementById('search-form');
const keywordInputEl = document.getElementById('keyword-input');
const locationEl = document.getElementById('location-input');

const fixedProjectCheckEl = document.getElementById('fixed-check');
const fixedProjectMinEl = document.getElementById('fixed-min');
const fixedProjectMaxEl = document.getElementById('fixed-max');
const fixedProjectErrorEl = document.getElementById('fixed-error');

const hourlyProjectCheckEl = document.getElementById('hourly-check');
const hourlyDurationSelectEl = document.getElementById('hourly-duration');
const hourlyProjectMinEl = document.getElementById('hourly-min');
const hourlyProjectMaxEl = document.getElementById('hourly-max');
const hourlyProjectErrorEl = document.getElementById('hourly-error');

const contestCheckEl = document.getElementById('contest-check');
const contestMinEl = document.getElementById('contest-min');
const contestMaxEl = document.getElementById('contest-max');
const contestErrorEl = document.getElementById('contest-error');

const sortEl = document.getElementById('table-sort-select');
const sortValues = Array.from(sortEl.options).map(el => {
  const value = JSON.parse(el.value);
  return {
    rawValue: el.value,
    value: {
      sortColumn: value[0],
      sortOrder: value[1],
      paramName: value[2],
    },
  };
});
const keywordResultsEl = document.getElementById('keyword-results');
const resultsPerPageEl = document.getElementById('table-row-count-select');
const totalResultsEl = document.getElementById('total-results');
const totalResultsBotEl = document.getElementById('total-results-bottom');
const firstEntryEl = document.getElementById('first-entry');
const lastEntryEl = document.getElementById('last-entry');
// Ordering
const pagination = new Pagination(['top-pagination', 'bottom-pagination'], {
  currPage: initialPageNumber,
  totalItems: initialTotalEntries,
  itemsPerPage: resultSetSize,
  onClickCallback: onPaginationClick,
});
// Initialise Empty SkillSelector
const skillsSelectorEl = document.getElementById('skills-selector');
const skillsSelector = new JobSearchSelector(skillsSelectorEl, {
  title: 'Skills',
  onSelectChangeCallback: () => {
    qtsp.push({
      eventName: 'user_action',
      section: 'page-job-search-new',
      subsection: 'filters',
      label: 'filter-skill',
    });
    onSearchChange();
  },
});
// Initialise Empty Languages Selector
const languagesSelectorEl = document.getElementById('languages-selector');
const languagesSelector = new JobSearchSelector(languagesSelectorEl, {
  title: 'Languages',
  onSelectChangeCallback: () => {
    qtsp.push({
      eventName: 'user_action',
      section: 'page-job-search-new',
      subsection: 'filters',
      label: 'filter-language',
    });
    onSearchChange();
  },
});

const statusFormEl = document.getElementById('status-form');

const recentSearch = new SearchHistory(
  'recent-searches-section',
  'recent-searches',
  'JOB_SEARCH',
  updateQueryBasedOnRecentSearch,
);

const filterBtnEl = document.getElementById('filter-btn');
const filterCloseBtnEl = document.getElementById('filter-close-btn');
const filterCloseLinkEl = document.getElementById('filter-close-link');
const filterPanelEl = document.getElementById('main');
// const mainContent = document.getElementById('main');
const createAlertLinkEl = document.getElementById('create-alert-link');

// Queries Ajax Endpoint and updates elements on page
function queryTableDataAndRender(jsonObject, pushToHistory) {
  return getRemoteData(jsonObject)
    .then(response => {
      // Do any updates necessary
      if (pagination.getTotalItems() !== response.iTotalDisplayRecords) {
        // Update pagination, reset to page 1
        pagination.setTotalItemsAndUpdate(
          response.iTotalDisplayRecords,
          pagination.getCurrPage(),
        );
        updateRecordsPerPage();
      }
      updatePageDetails(
        jsonObject.tag,
        jsonObject.vicinity,
        pagination.getCurrPage(),
        pushToHistory,
      );
      return response;
    })
    .then(response => {
      // Render the Table
      renderTable(
        'project-list',
        response.aaData,
        showPostBtn,
        showBidBtn,
        onItemClick,
      );
      const parent = document.getElementById('other_related');
      parent.innerHTML = template(otherRelatedTemplate)({
        tags_keyword: jsonObject.tag,
        projectTags: response.tagData,
        projectTagsLast: response.projectTagsLast,
      });
    })
    .catch(() => {});
}

function onItemClick(event) {
  window.location = event.currentTarget.querySelector(
    '[data-heading-link="true"]',
  ).href;
}

/**
 * Helper function to get the project type to parse to backend
 * @return [false, f, h, c, p, hc, fc]
 *          false = all
 *          f = fixed
 *          h = hourly
 *          p = fixed and hourly
 *          hc = hourly and contest
 *          fc = fixed and contest
 */
function getProjectType() {
  if (checkAllProjectTypesSelected()) {
    return false;
  } else if (fixedProjectCheckEl.checked && contestCheckEl.checked) {
    return 'fc';
  } else if (hourlyProjectCheckEl.checked && contestCheckEl.checked) {
    return 'hc';
  } else if (fixedProjectCheckEl.checked && hourlyProjectCheckEl.checked) {
    return 'p';
  } else if (hourlyProjectCheckEl.checked) {
    return 'h';
  } else if (fixedProjectCheckEl.checked) {
    return 'f';
  } else if (contestCheckEl.checked) {
    return 'c';
  }
  return false;
}

function checkAllProjectTypesSelected() {
  if (
    fixedProjectCheckEl.checked &&
    hourlyProjectCheckEl.checked &&
    contestCheckEl.checked
  ) {
    return true;
  } else if (
    !fixedProjectCheckEl.checked &&
    !hourlyProjectCheckEl.checked &&
    !contestCheckEl.checked
  ) {
    return true;
  }
  return false;
}

function onSearchChange(e, pushToHistory = true) {
  if (e) {
    const elementType = e.target.type;
    qtsp.push({
      eventName: 'user_action',
      section: 'page-job-search-new',
      subsection: `budget-filter-${elementType}`,
      label: 'others',
    });
  }
  if (!validateSearchFilters()) {
    return;
  }

  // Get Results Per Page
  const resultsPerPageInt = parseInt(resultsPerPageEl.value, 10);

  // Construct Query Data
  const queryData = {};
  // search keyword
  queryData.tag = searchFormEl.search_keyword.value.trim();
  // project specs
  queryData.type = getProjectType();
  const allProjectTypesSelected = checkAllProjectTypesSelected();
  // exchange rate required to convert input (expected to be in local currenct)
  // to USD, which is expected by the backend
  if (allProjectTypesSelected || fixedProjectCheckEl.checked) {
    queryData.budget_min = getValueForQuery(fixedProjectMinEl.value);
    queryData.budget_max = getValueForQuery(fixedProjectMaxEl.value);
  }
  if (allProjectTypesSelected || contestCheckEl.checked) {
    queryData.contest_budget_min = getValueForQuery(contestMinEl.value);
    queryData.contest_budget_max = getValueForQuery(contestMaxEl.value);
  }
  if (allProjectTypesSelected || hourlyProjectCheckEl.checked) {
    queryData.hourlyrate_min = getValueForQuery(hourlyProjectMinEl.value);
    queryData.hourlyrate_max = getValueForQuery(hourlyProjectMaxEl.value);
    queryData.hourlyProjectDuration = hourlyDurationSelectEl.value;
  }
  queryData.skills_chosen = getSkillIdsFromSelectedSkills() || false;
  queryData.languages = getLanguageCodesFromSelectedLanguages() || false;
  queryData.status = statusFormEl.status.value;
  // search location
  queryData.vicinity = searchFormEl.vicinity.value || false;
  queryData.countries = searchFormEl.country.value || false;
  queryData.lat = searchFormEl.lat.value || false;
  queryData.lon = searchFormEl.lon.value || false;
  if (queryData.lat && queryData.lon) {
    queryData.local = 'true';
  }
  // table properties
  queryData.iDisplayStart = (pagination.getCurrPage() - 1) * resultsPerPageInt;
  queryData.iDisplayLength = resultsPerPageInt;
  queryData.iSortingCols = 1; // default
  const sortingData = JSON.parse(sortEl.value);
  queryData.iSortCol_0 = sortingData[0];
  queryData.sSortDir_0 = sortingData[1];
  queryData.format_version = 2;

  queryTableDataAndRender(queryData, pushToHistory);
}

function getValueForQuery(value) {
  if (value === '' || Number.isNaN(value)) {
    return false;
  }
  return exchangerate * parseFloat(value);
}

function updateRecordsPerPage() {
  const totalItems = pagination.getTotalItems();
  // Set total results
  totalResultsEl.textContent = totalItems.toLocaleString();
  totalResultsBotEl.textContent = totalItems.toLocaleString();
  // First entry
  if (totalItems === 0) {
    firstEntryEl.textContent = 0;
  } else {
    firstEntryEl.textContent =
      (pagination.getCurrPage() - 1) * pagination.getItemsPerPage() + 1;
  }
  // Last Entry
  if (totalItems === 0) {
    lastEntryEl.textContent = 0;
  } else {
    let lastEntry = pagination.getCurrPage() * pagination.getItemsPerPage();
    if (pagination.getTotalItems() < lastEntry) {
      lastEntry = pagination.getTotalItems();
    }
    lastEntryEl.textContent = lastEntry;
  }
}

function onPaginationClick(page) {
  qtsp.push({
    eventName: 'user_action',
    section: 'page-job-search-new',
    subsection: 'pagination',
    label: 'click-page',
  });
  pagination.setCurrPageAndUpdate(page);
  updateRecordsPerPage();
  onSearchChange();
  scrollOnPageUpdate(document.documentElement, 0, SCROLL_SPEED);
}

function scrollOnPageUpdate(element, toLocation, scrollTime) {
  if (scrollTime <= 0) return;
  const difference = toLocation - element.scrollTop;
  const perTick = difference / scrollTime * 10;

  setTimeout(() => {
    element.scrollTop += perTick;
    if (element.scrollTop == toLocation) return;
    scrollOnPageUpdate(element, toLocation, scrollTime - 10);
  }, 10);
}

function onSortingChange() {
  qtsp.push({
    eventName: 'user_action',
    section: 'page-job-search-new',
    subsection: 'sort-criteria',
    label: 'criteria-select',
  });
  pagination.setCurrPageAndUpdate(1);
  onSearchChange();
}

function onResultPerPageChange() {
  qtsp.push({
    eventName: 'user_action',
    section: 'page-job-search-new',
    subsection: 'results-per-page',
    label: 'results-select',
  });
  const resultsPerPageInt = parseInt(resultsPerPageEl.value, 10);
  if (!Number.isNaN(resultsPerPageInt)) {
    // Update Pagination (itemsPerPage change), reset curr page to 1
    pagination.setItemsPerPageAndUpdate(resultsPerPageInt, 1);
    updateRecordsPerPage();
    // Query again
    onSearchChange();
    scrollOnPageUpdate(document.documentElement, 0, SCROLL_SPEED);
  }
}

function onProjectTypeChange(e = null) {
  handleProjectTypeSelections();
  onSearchChange(e);
}

function handleProjectTypeSelections() {
  const allProjectTypesSelected = checkAllProjectTypesSelected();
  const enableFixedProjects =
    allProjectTypesSelected || fixedProjectCheckEl.checked;
  const enableHourlyProjects =
    allProjectTypesSelected || hourlyProjectCheckEl.checked;
  const enableContests = allProjectTypesSelected || contestCheckEl.checked;

  onFixedProjectChange(enableFixedProjects);
  onHourlyChange(enableHourlyProjects);
  onContestChange(enableContests);
}

function onFixedProjectChange(enable = false) {
  if (enable) {
    fixedProjectMaxEl.removeAttribute('disabled');
    fixedProjectMinEl.removeAttribute('disabled');
  } else {
    fixedProjectMaxEl.setAttribute('disabled', 'disabled');
    fixedProjectMinEl.setAttribute('disabled', 'disabled');
  }
}

function onHourlyChange(enable = false) {
  // If hourly projects is selected then enable the two selections if exists
  if (enable) {
    hourlyProjectMinEl.removeAttribute('disabled');
    hourlyProjectMaxEl.removeAttribute('disabled');
    hourlyDurationSelectEl.removeAttribute('disabled');
  } else {
    hourlyProjectMinEl.setAttribute('disabled', 'disabled');
    hourlyProjectMaxEl.setAttribute('disabled', 'disabled');
    hourlyDurationSelectEl.setAttribute('disabled', 'disabled');
  }
}

function onContestChange(enable = false) {
  // If contest is selected then enable the two selections if exists
  if (enable) {
    contestMinEl.removeAttribute('disabled');
    contestMaxEl.removeAttribute('disabled');
  } else {
    contestMinEl.setAttribute('disabled', 'disabled');
    contestMaxEl.setAttribute('disabled', 'disabled');
  }
}

function clearValidationErrorMessages() {
  fixedProjectErrorEl.innerText = '';
  fixedProjectErrorEl.classList.add('is-hidden');
  hourlyProjectErrorEl.innerText = '';
  hourlyProjectErrorEl.classList.add('is-hidden');
  contestErrorEl.innerText = '';
  contestErrorEl.classList.add('is-hidden');
}

// Validate Search filters which need validating
function validateSearchFilters() {
  const allProjectTypesSelected = checkAllProjectTypesSelected();
  let allFiltersValid = true;
  clearValidationErrorMessages();
  // Fixed Price Projects Check
  if (allProjectTypesSelected || fixedProjectCheckEl.checked) {
    const fixedProjectFieldsValidation = handleValidationAndErrorMessage(
      fixedProjectMinEl,
      fixedProjectMaxEl,
      fixedProjectErrorEl,
    );
    allFiltersValid = allFiltersValid && fixedProjectFieldsValidation;
  }
  // Hourly Projects Check
  if (allProjectTypesSelected || hourlyProjectCheckEl.checked) {
    const hourlyProjectFieldsValidation = handleValidationAndErrorMessage(
      hourlyProjectMinEl,
      hourlyProjectMaxEl,
      hourlyProjectErrorEl,
    );
    allFiltersValid = allFiltersValid && hourlyProjectFieldsValidation;
  }
  // Contests Check
  if (allProjectTypesSelected || contestCheckEl.checked) {
    const contestFieldsValidation = handleValidationAndErrorMessage(
      contestMinEl,
      contestMaxEl,
      contestErrorEl,
    );
    allFiltersValid = allFiltersValid && contestFieldsValidation;
  }
  return allFiltersValid;
}

function handleValidationAndErrorMessage(minEl, maxEl, errorEl) {
  const validation = validateNumericField(minEl, maxEl);
  if (!validation.isValid) {
    errorEl.innerText = validation.errorMessage;
    errorEl.classList.remove('is-hidden');
    return false;
  }
  return true;
}

function validateNumericField(filterMinEl, filterMaxEl) {
  const validation = {
    isValid: false,
    errorMessage: null,
  };
  const min = parseFloat(filterMinEl.value);
  const max = parseFloat(filterMaxEl.value);
  if (Number.isNaN(min) && filterMinEl.value !== '') {
    validation.errorMessage = T_`Minimum value must be a valid number`;
    return validation;
  } else if (Number.isNaN(max) && filterMaxEl.value !== '') {
    validation.errorMessage = T_`Maximum value must be a valid number`;
    return validation;
  }
  if (min < 0 && min !== '') {
    validation.errorMessage = T_`Minimum value cannot be negative`;
    return validation;
  } else if (max < 0 && max !== '') {
    validation.errorMessage = T_`Maximum value cannot be negative`;
    return validation;
  } else if (min > max) {
    validation.errorMessage = T_`Minimum value must be smaller than maximum value`;
    return validation;
  }
  validation.isValid = true;
  return validation;
}

// Gets selected skills  and reformats them to return a list of ids
function getSkillIdsFromSelectedSkills() {
  const skillsList = skillsSelector.getSelectedItems();
  let count = 0;
  return skillsList.reduce((total, item) => {
    count += 1;
    if (count < skillsList.length) {
      return `${total + item.job_id},`;
    }
    return total + item.job_id;
  }, '');
}

// Gets selected languages and reformats them to returns a list of codes
function getLanguageCodesFromSelectedLanguages() {
  const languagesList = languagesSelector.getSelectedItems();
  let count = 0;
  return languagesList.reduce((total, item) => {
    count += 1;
    if (count < languagesList.length) {
      return `${total + item.code},`;
    }
    return total + item.code;
  }, '');
}

function showFilter() {
  filterPanelEl.classList.add('is-SectionJobFilter-active');
}

function hideFilter() {
  filterPanelEl.classList.remove('is-SectionJobFilter-active');
}

/**
 * Callback when the location is chosen from the maps API
 * @param {formatted_address, lat, lon, vicinity, country} geoInfo
 */
function onLocationSelected(geoInfo) {
  qtsp.push({
    eventName: 'user_action',
    section: 'page-job-search-new',
    subsection: 'search-bar',
    label: 'field-location',
  });

  // update hidden form for location
  searchFormEl.vicinity.value = geoInfo.vicinity;
  searchFormEl.state_code.value = geoInfo.administrative_area_level_1;
  searchFormEl.country.value = geoInfo.country;
  searchFormEl.lat.value = geoInfo.lat;
  searchFormEl.lon.value = geoInfo.lng;
}

/**
 * Small UI logic when the user focusses on the location input field,
 * the DEFAULT_LOCATION that is prefilled is removed
 */
function onLocationFieldFocusIn() {
  if (locationEl.value === DEFAULT_LOCATION) {
    // clear out the value so the autocomplete can suggest something relevant
    locationEl.value = '';
  }
}

/**
 * If the search location is removed, reset the location search to empty
 * Also updates the title
 */
function onLocationFieldFocusOut() {
  if (locationEl.value === '') {
    // set default value if location is emptied
    locationEl.value = DEFAULT_LOCATION;

    searchFormEl.vicinity.value = '';
    searchFormEl.state_code.value = '';
    searchFormEl.country.value = '';
    searchFormEl.lat.value = '';
    searchFormEl.lon.value = '';
  }
}

/**
 * Update title on the hero section based on keyword and location entered
 */
function updatePageDetails(query, location, pageNum, pushToHistory) {
  let pageTitle;
  let skillsName = '';
  if (pageName === JOBS_PAGE) {
    skillsName = skillsSelector
      .getSelectedItems()
      .map(item => item.name)
      .join(' ');
  } else if (query.trim()) {
    skillsName = query.trim();
    skillsName = skillsName.charAt(0).toUpperCase() + skillsName.slice(1);
  }
  keywordResultsEl.textContent = `${skillsName}`;
  if (skillsName && location) {
    pageTitle = T_`${skillsName} jobs in ${location}`;
  } else if (skillsName && !location) {
    pageTitle = T_`${skillsName} jobs`;
  } else if (!skillsName && location) {
    pageTitle = T_`Top Jobs in ${location}`;
  } else {
    pageTitle = T_`Top Jobs`;
  }
  jobLocationHeroTitleEl.innerText = pageTitle;
  document.title = `${pageTitle} - ${siteName}`;
  const pathUrl =
    pageName === JOBS_PAGE
      ? buildUrlForJobsPage(skillsSelector.getSelectedItems(), pageNum)
      : buildUrl(encodeURIComponent(query), pageNum);
  const newQueryUrl = buildUrlQueryFromFilters(window.location.search, pathUrl);
  updateBreadcrumbs(skillsName || query.trim(), pageNum, pathUrl);
  if (pushToHistory) {
    pushNewHistoryState(newQueryUrl);
  }
}

function buildUrl(query, pageNum) {
  const searchTagString = decodeURIComponent(query)
    .split(' ')
    .join('-');
  const currentUrl = window.location;
  const pageName = currentUrl.pathname.split('/')[1];
  let newUrl = [pageName, searchTagString];
  if (pageNum > 1) {
    newUrl.push(pageNum);
  }
  newUrl = newUrl.join('/');
  return `/${newUrl}/`;
}

function buildUrlForJobsPage(skills, pageNum) {
  let skills_url = 'regions';
  if (skills.length) {
    skills_url = skills.reduce(
      (total, skill) =>
        total === ''
          ? skill.seo_url.split('/')[2]
          : `${total}_${skill.seo_url.split('/')[2]}`,
      '',
    );
  }
  const pageName = window.location.pathname.split('/')[1];
  let newUrl = [pageName, skills_url];
  if (pageNum > 1) {
    newUrl.push(pageNum);
  }
  newUrl = newUrl.join('/');
  return `/${newUrl}/`;
}

function updateBreadcrumbs(query, pageNum, newUrl) {
  if (pageName === JOBS_PAGE) {
    breadcrumbSearchTitleEl.textContent = query.split(' ')[0];
    if (breadcrumbSearchTitleEl.textContent === '') {
      breadcrumbSearchTitleEl.textContent = T_('Browse All Jobs');
    }
  } else {
    breadcrumbSearchTitleEl.textContent = query;
  }
  breadcrumbSearchPageEl.textContent = pageNum;
  breadcrumbSearchTitleUrlEl.href = newUrl;
}

function pushNewHistoryState(newUrl) {
  window.history.pushState(null, '', newUrl);
}

/**
 * Create a URL string from the search filter and taking into account
 * the rest of the parameters already in the original url
 * @param string currQuery The current query e.g. ?t=x&bla=foo
 * @param string newPathUrl The new url e.g. /job-search/php/
 */
function buildUrlQueryFromFilters(currQuery, newPathUrl) {
  // convert parameters from url to json object
  const data = convertParametersFromUrl(currQuery);

  if (pageName === JOBS_PAGE) {
    if (searchFormEl.search_keyword.value !== '') {
      data.keyword = encodeURIComponent(searchFormEl.search_keyword.value);
    } else {
      delete data.keyword;
    }
  }

  const sortingData = JSON.parse(sortEl.value);
  if (sortingData[2] !== sortValues[0].value.paramName) {
    data.sort = sortingData[2];
  } else {
    delete data.sort;
  }

  if (searchFormEl.vicinity.value !== '') {
    data.vic = searchFormEl.vicinity.value;
  } else {
    delete data.vic;
  }
  if (searchFormEl.country.value !== '') {
    data.country = searchFormEl.country.value;
  } else {
    delete data.country;
  }
  if (searchFormEl.lat.value !== '') {
    data.lat = searchFormEl.lat.value;
  } else {
    delete data.lat;
  }
  if (searchFormEl.lon.value !== '') {
    data.lon = searchFormEl.lon.value;
  } else {
    delete data.lon;
  }

  if (fixedProjectCheckEl.checked === true) {
    data.fixed = true;
  } else {
    delete data.fixed;
  }
  if (fixedProjectMinEl.value !== '') {
    data.fixed_min = fixedProjectMinEl.value * exchangerate;
  } else {
    delete data.fixed_min;
  }
  if (fixedProjectMaxEl.value !== '') {
    data.fixed_max = fixedProjectMaxEl.value * exchangerate;
  } else {
    delete data.fixed_max;
  }

  if (hourlyProjectCheckEl.checked === true) {
    data.hourly = true;
  } else {
    delete data.hourly;
  }
  if (hourlyProjectMinEl.value !== '') {
    data.hourly_min = hourlyProjectMinEl.value * exchangerate;
  } else {
    delete data.hourly_min;
  }
  if (hourlyProjectMaxEl.value !== '') {
    data.hourly_max = hourlyProjectMaxEl.value * exchangerate;
  } else {
    delete data.hourly_max;
  }
  if (hourlyDurationSelectEl.value !== DEFAULT_HOURLY_DURATION) {
    data.hourly_duration = hourlyDurationSelectEl.value;
  } else {
    delete data.hourly_duration;
  }

  if (contestCheckEl.checked === true) {
    data.contest = true;
  } else {
    delete data.contest;
  }
  if (contestMinEl.value !== '') {
    data.contest_min = contestMinEl.value / exchangerate;
  } else {
    delete data.contest_min;
  }
  if (contestMaxEl.value !== '') {
    data.contest_max = contestMaxEl.value / exchangerate;
  } else {
    delete data.contest_max;
  }
  if (parseInt(resultsPerPageEl.value, 10) !== defaultResultSetSize) {
    data.results = resultsPerPageEl.value;
  } else {
    delete data.results;
  }

  if (pageName !== JOBS_PAGE) {
    const skills_chosen = getSkillIdsFromSelectedSkills();
    if (skills_chosen !== '') {
      data.skills = skills_chosen;
    } else {
      delete data.skills;
    }
  }

  const languages = getLanguageCodesFromSelectedLanguages();
  if (languages !== '') {
    data.languages = languages;
  } else {
    delete data.languages;
  }

  if (
    (pageName === JOBS_PAGE && statusFormEl.status.value !== 'open') ||
    (pageName !== JOBS_PAGE && statusFormEl.status.value !== 'all')
  ) {
    data.status = statusFormEl.status.value;
  } else {
    delete data.status;
  }
  const queryString = jsonToQueryString(data);
  return queryString ? `${newPathUrl}?${queryString}` : newPathUrl;
}

function jsonToQueryString(json) {
  return Object.keys(json)
    .map(key => `${key}=${json[key]}`)
    .join('&');
}

function convertParametersFromUrl(searchString) {
  searchString = searchString.replace('?', '');
  if (searchString == '') {
    return {};
  }
  const params = searchString.split('&').reduce((paramObj, param) => {
    const parameter = param.split('=');
    if (parameter[0] && parameter[1]) {
      paramObj[parameter[0]] = parameter[1];
    }
    return paramObj;
  }, {});
  if (!params) {
    return {};
  }
  return params;
}

function restoreFiltersFromUrlQuery() {
  const url = window.location;
  const query = url.pathname.split('/')[2];
  const pageNum = url.pathname.split('/')[3];
  const urlSearchString = url.search.substr(1);
  const params = convertParametersFromUrl(urlSearchString);
  if (pageName === JOBS_PAGE) {
    searchFormEl.search_keyword.value = params.keyword
      ? decodeURIComponent(params.keyword).trim()
      : '';
  } else {
    searchFormEl.search_keyword.value = query
      ? decodeURIComponent(query.replace(/-/g, ' ')).trim()
      : '';
  }
  pagination.setCurrPageAndUpdate(pageNum);

  const sortOption = sortValues.find(
    elem => elem.value.paramName === params.sort,
  );
  if (params.sort === sortValues[0].value.paramName || !sortOption) {
    sortEl.value = sortValues[0].rawValue;
  } else {
    sortEl.value = sortOption.rawValue;
  }

  const resultSize = Array.from(resultsPerPageEl.options).find(
    elem => elem.value === params.results,
  );
  if (params.results && resultSize) {
    resultsPerPageEl.value = resultSize.value;
  } else {
    resultsPerPageEl.value = defaultResultSetSize;
  }

  const isLocationValid = basicLocationValidation(params);
  searchFormEl.vicinity.value = isLocationValid && params.vic ? params.vic : '';
  searchFormEl.country.value =
    isLocationValid && params.country ? params.country : '';
  searchFormEl.lat.value = isLocationValid && params.lat ? params.lat : '';
  searchFormEl.lon.value = isLocationValid && params.lon ? params.lon : '';
  locationEl.value =
    isLocationValid && params.vic ? params.vic : DEFAULT_LOCATION;

  fixedProjectCheckEl.checked = params.fixed === 'true';
  fixedProjectMinEl.value = getValueFromUrl(params.fixed_min);
  fixedProjectMaxEl.value = getValueFromUrl(params.fixed_max);

  hourlyProjectCheckEl.checked = params.hourly === 'true';
  hourlyProjectMinEl.value = getValueFromUrl(params.hourly_min);
  hourlyProjectMaxEl.value = getValueFromUrl(params.hourly_max);
  const hourlyDuration = Array.from(hourlyDurationSelectEl.options).find(
    elem => elem.value === params.hourly_duration,
  );
  if (params.hourly_duration && hourlyDuration) {
    hourlyDurationSelectEl.value = hourlyDuration.value;
  } else {
    hourlyDurationSelectEl.value = DEFAULT_HOURLY_DURATION;
  }

  contestCheckEl.checked = params.contest === 'true';
  contestMinEl.value = getValueFromUrl(params.contest_min);
  contestMaxEl.value = getValueFromUrl(params.contest_max);

  restoreLanguageFiltersFromUrlQuery();
  restoreSkillFiltersFromUrlQuery();

  if (pageName === JOBS_PAGE) {
    statusFormEl.status.value = params.status ? params.status : 'open';
  } else {
    statusFormEl.status.value = params.status ? params.status : 'all';
  }

  handleProjectTypeSelections();
  validateSearchFilters();
}

function getValueFromUrl(value) {
  if (!value || value === '') {
    return '';
  }
  if (Number.isNaN(value)) {
    return value;
  }
  return parseFloat(value) / exchangerate;
}

function restoreLanguageFiltersFromUrlQuery() {
  languagesSelector.removeAllSelectedItems();
  const urlSearchString = window.location.search.substr(1);
  const params = convertParametersFromUrl(urlSearchString);

  const languages = languagesSelector.getItemList();
  const languageParams = params.languages ? params.languages.split(',') : [];
  languageParams.forEach(lang => {
    const languageItem = languages.find(item => item.code === lang);
    if (languageItem) {
      languagesSelector.selectItemByName(languageItem.name);
    }
  });
}

function basicLocationValidation(params) {
  if (!params || !params.vic) {
    return false;
  }
  const lat = parseFloat(params.lat);
  const lon = parseFloat(params.lon, 10);
  if (Number.isNaN(lat) || Number.isNaN(lon)) {
    return false;
  }
  if (lat > 90 || lat < -90 || lon > 180 || lon < -180) {
    return false;
  }
  return true;
}

function restoreSkillFiltersFromUrlQuery() {
  skillsSelector.removeAllSelectedItems();
  const skills = skillsSelector.getItemList();
  if (pageName !== JOBS_PAGE) {
    const urlSearchString = window.location.search.substr(1);
    const params = convertParametersFromUrl(urlSearchString);

    const skillParams = params.skills ? params.skills.split(',') : [];
    skillParams.forEach(skill => {
      const skillItem = skills.find(item => item.job_id === skill);
      if (skillItem) {
        skillsSelector.selectItemByName(skillItem.name);
      }
    });
  } else {
    const skillParams = window.location.pathname.split('/')[2].split('_');
    skillParams.forEach(skill => {
      const skillItem = skills.find(
        item => item.seo_url.split('/')[2] === skill.toLowerCase(),
      );
      if (skillItem) {
        skillsSelector.selectItemByName(skillItem.name);
      }
    });
  }
}

/**
 * Callback when user click on the recent searches links
 * Then calls onSearchChange
 * @param {keyword, location} param0
 */
function updateQueryBasedOnRecentSearch({ keyword, location }) {
  qtsp.push({
    eventName: 'user_action',
    section: 'page-job-search-new',
    subsection: 'recent-search',
    label: 'click',
  });

  searchFormEl.search_keyword.value = keyword;
  searchFormEl.vicinity.value = location.vicinity;
  searchFormEl.country.value = location.country;
  searchFormEl.lat.value = location.lat;
  searchFormEl.lon.value = location.lon;
  locationEl.value = location.vicinity || DEFAULT_LOCATION;
  onSearchChange();
}

/**
 * Initialization and binding of the job alert modal
 */
function initAlertModal() {
  // Initialize the popover
  const modalTemplate = {
    header: template(jobAlertModalHeaderTemplate)({
      T_,
    }),

    body: template(jobAlertModalTemplate)({
      T_,
      gotoLink: gotoUrl,
    }),
  };
  const alertModal = new Modal('job-alert-modal', modalTemplate, {
    size: MODAL_SIZE.SMALL,
  });

  if (createAlertLinkEl) {
    createAlertLinkEl.addEventListener('click', event => {
      event.preventDefault();
      alertModal.show();
    });
  }
}

function initLocationSearch() {
  pushHistory();
  createGeocoding({
    autoCompleteElement: locationEl.id,
    autoCompleteCallback: onLocationSelected,
    autoCompletePreference: AUTOCOMPLETE_PREFERENCE.vicinity,
  });
}

function pushHistory(popFirst = false) {
  if (popFirst) {
    recentSearch.pop();
  }
  const query = searchFormEl.search_keyword.value.toLowerCase();
  recentSearch.push({
    keyword: query,
    location: {
      country: searchFormEl.country.value,
      vicinity: searchFormEl.vicinity.value,
      lat: searchFormEl.lat.value,
      lon: searchFormEl.lon.value,
    },
  });
}

function bindElements() {
  if (locationEl) {
    // hook into the on focus event, to remove or update the field value
    locationEl.addEventListener('focusin', () => onLocationFieldFocusIn());
    locationEl.addEventListener('focusout', () => onLocationFieldFocusOut());
    locationEl.addEventListener('keypress', e => {
      if (e.keyCode === 13) {
        e.preventDefault();
      }
    });
  }
  filterBtnEl.addEventListener('click', e => showFilter(e));
  filterCloseBtnEl.addEventListener('click', e => hideFilter(e));
  filterCloseLinkEl.addEventListener('click', e => hideFilter(e));
  searchFormEl.addEventListener('submit', e => {
    e.preventDefault();
    pushHistory();
    onSearchChange(e);
  });

  // for mobile - when the search keyword is focus, show the location
  keywordInputEl.addEventListener('focusin', () => {
    searchFormEl.classList.add('is-onlineSearch--active');
  });

  // set page length selector to default value initially
  resultsPerPageEl.value = defaultResultSetSize;

  // Fixed Price Project Elements
  fixedProjectCheckEl.addEventListener('change', e => onProjectTypeChange(e));
  fixedProjectMinEl.addEventListener(
    'input',
    debounce(e => onSearchChange(e), 600),
  );
  fixedProjectMaxEl.addEventListener(
    'input',
    debounce(e => onSearchChange(e), 600),
  );

  // Hourly Project Elements
  hourlyProjectCheckEl.addEventListener('change', e => onProjectTypeChange(e));
  hourlyDurationSelectEl.addEventListener('change', e => onSearchChange(e));
  hourlyProjectMinEl.addEventListener(
    'input',
    debounce(e => onSearchChange(e), 600),
  );
  hourlyProjectMaxEl.addEventListener(
    'input',
    debounce(e => onSearchChange(e), 600),
  );

  // Contest Elements
  contestCheckEl.addEventListener('change', e => onProjectTypeChange(e));
  contestMinEl.addEventListener('input', debounce(e => onSearchChange(e), 600));
  contestMaxEl.addEventListener('input', debounce(e => onSearchChange(e), 600));
  sortEl.addEventListener('change', () => onSortingChange());
  resultsPerPageEl.addEventListener('change', () => onResultPerPageChange());
  statusFormEl.addEventListener('change', () => onSearchChange());

  bindTableCard(onItemClick);
}

function initItemsForSkillsAndLanguages() {
  fetch('/ajax/search/allSkills.php')
    .then(data => data.json())
    .then(data => {
      skillsSelector.setItemList(data.results, [
        'PHP',
        'HTML',
        'Javascript',
        'CSS',
        'Mobile Phone',
      ]);
      restoreSkillFiltersFromUrlQuery();
    })
    .catch();

  fetch('/ajax/search/getActiveLanguages.php')
    .then(data => data.json())
    .then(data => {
      languagesSelector.setItemList(data.results, [
        'English',
        'Spanish',
        'German',
        'French',
        'Portuguese',
      ]);
      restoreLanguageFiltersFromUrlQuery();
    })
    .catch();
}

window.addEventListener('popstate', () => {
  restoreFiltersFromUrlQuery();
  onSearchChange(null, false);
});

function init() {
  bindElements();
  initLocationSearch();
  if (showAlertModal) {
    initAlertModal();
  }
  initItemsForSkillsAndLanguages();
  restoreFiltersFromUrlQuery();
}
init();
